import { useEffect, useRef, useState } from "react";
import { Pause, PlayArrow, VolumeDown, VolumeOff, VolumeUp } from "@mui/icons-material";
import { IconButton, Popover, Slider } from "@mui/material";

import { IMedia } from "@/interfaces/media.interface";

interface AudioProps {
  media: IMedia;
  mediaURL: string;
  isSentByUser?: boolean;
}

const SPEEDS = [1, 1.5, 2] as const;

const resolveAudioMime = (extension: string | undefined): string => {
  const ext = (extension || "").toLowerCase();
  if (ext === "mp3") return "audio/mpeg";
  if (ext === "ogg" || ext === "oga" || ext === "opus") return "audio/ogg";
  if (ext === "wav") return "audio/wav";
  if (ext === "webm") return "audio/webm";
  if (ext === "m4a" || ext === "mp4a" || ext === "aac") return "audio/mp4";
  return `audio/${ext || "mpeg"}`;
};

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const AudioMedia: React.FC<AudioProps> = ({ media, mediaURL, isSentByUser }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [speedIdx, setSpeedIdx] = useState(0);
  const volumeBtnRef = useRef<HTMLButtonElement | null>(null);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const mime = resolveAudioMime(media.extension);

  const foreground = isSentByUser ? "#ffffff" : "#16D3D9";
  const trackBg = isSentByUser ? "rgba(255,255,255,0.35)" : "rgba(22,211,217,0.25)";
  const hoverBg = isSentByUser ? "rgba(255,255,255,0.15)" : "rgba(22,211,217,0.12)";

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => setCurrent(el.currentTime);
    const onLoaded = () => setDuration(el.duration || 0);
    const onEnded = () => {
      setPlaying(false);
      setCurrent(0);
    };

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("durationchange", onLoaded);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("durationchange", onLoaded);
      el.removeEventListener("ended", onEnded);
    };
  }, [mediaURL]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[speedIdx];
  }, [speedIdx]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const handleSeek = (_: Event, value: number | number[]) => {
    const el = audioRef.current;
    if (!el) return;
    const v = Array.isArray(value) ? value[0] : value;
    el.currentTime = v;
    setCurrent(v);
  };

  const cycleSpeed = () => setSpeedIdx((i) => (i + 1) % SPEEDS.length);
  const display = playing || current > 0 ? current : duration;
  const speed = SPEEDS[speedIdx];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        minWidth: 260,
        maxWidth: 340,
        padding: "2px 4px",
      }}
    >
      <IconButton
        onClick={togglePlay}
        size="small"
        sx={{ color: foreground, padding: "12px", "&:hover": { background: hoverBg } }}
        aria-label={playing ? "pause" : "play"}
      >
        {playing ? <Pause sx={{ fontSize: 32 }} /> : <PlayArrow sx={{ fontSize: 32 }} />}
      </IconButton>

      <Slider
        value={current}
        min={0}
        max={duration || 0}
        step={0.1}
        onChange={handleSeek}
        sx={{
          color: foreground,
          height: 6,
          padding: "12px 0",
          "& .MuiSlider-rail": { backgroundColor: trackBg, opacity: 1, height: 6 },
          "& .MuiSlider-track": { border: "none", height: 6 },
          "& .MuiSlider-thumb": {
            width: 14,
            height: 14,
            backgroundColor: foreground,
            "&:hover, &.Mui-focusVisible": { boxShadow: `0 0 0 6px ${hoverBg}` },
          },
        }}
      />

      <span
        style={{
          color: foreground,
          fontSize: 12,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          minWidth: 36,
          textAlign: "right",
        }}
      >
        {formatTime(display)}
      </span>

      <button
        type="button"
        onClick={cycleSpeed}
        aria-label={`playback speed ${speed}x`}
        style={{
          color: foreground,
          background: "transparent",
          border: `2px solid ${foreground}`,
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 700,
          padding: "3px 8px",
          minWidth: 44,
          cursor: "pointer",
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {speed}x
      </button>

      <IconButton
        ref={volumeBtnRef}
        onClick={() => setVolumeOpen(true)}
        onContextMenu={(e) => {
          e.preventDefault();
          setMuted((m) => !m);
        }}
        size="small"
        sx={{ color: foreground, padding: "6px", "&:hover": { background: hoverBg } }}
        aria-label="volume"
      >
        {muted || volume === 0 ? (
          <VolumeOff sx={{ fontSize: 20 }} />
        ) : volume < 0.5 ? (
          <VolumeDown sx={{ fontSize: 20 }} />
        ) : (
          <VolumeUp sx={{ fontSize: 20 }} />
        )}
      </IconButton>


      <Popover
        open={volumeOpen}
        anchorEl={volumeBtnRef.current}
        onClose={() => setVolumeOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              borderRadius: 2,
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              background: "#16D3D9",
            },
          },
        }}
      >
        <div style={{ height: 110, display: "flex", justifyContent: "center", position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: 4,
              backgroundColor: "#ffffff",
              borderRadius: 2,
              pointerEvents: "none",
            }}
          />
          <Slider
            orientation="vertical"
            value={muted ? 0 : volume}
            min={0}
            max={1}
            step={0.01}
            onChange={(_, v) => {
              const val = Array.isArray(v) ? v[0] : v;
              setVolume(val);
              if (val > 0 && muted) setMuted(false);
              if (val === 0 && !muted) setMuted(true);
            }}
            sx={{
              color: "transparent",
              width: 20,
              padding: "0 8px",
              position: "relative",
              zIndex: 1,
              "& .MuiSlider-rail": { display: "none" },
              "& .MuiSlider-track": { display: "none" },
              "& .MuiSlider-thumb": {
                width: 14,
                height: 14,
                backgroundColor: "#ffffff",
                "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 6px rgba(255,255,255,0.25)" },
              },
            }}
          />
        </div>
      </Popover>


      <audio ref={audioRef} preload="metadata" style={{ display: "none" }}>
        <source src={mediaURL} type={mime} />
      </audio>
    </div>
  );
};

export default AudioMedia;
